import React, { RefObject, useCallback, useEffect, useState } from 'react';
import { FaStream } from 'react-icons/fa';

import { Box, Flex, Text, useToast, Tooltip } from '@chakra-ui/react';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { useAuth } from '~/hooks/auth';
import { Attendance } from '~/modules/activityExecution/@types/activity';
import { Filters } from '~/modules/management/pages/Attendances';
import api from '~/shared/services/api';
import getAttendanceFilters from '~/utils/getAttendanceFilters';
import getValidationErrors from '~/utils/getValidationErrors';

import InputChakra from '../InputChakra';
import SelectChakra from '../InputChakra/SelectChakra';
import TextareaChakra from '../InputChakra/Textarea';
import ModalChakra from '../Modal/ChakraUI';

interface NewAttendanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  getNewAttendance: (attendance: Attendance) => void;
  title: string;
  formRef: RefObject<any>;
  byEmployee?: boolean;
}

/* export interface NewAttendance extends Attendance {
  requester: string;
  demand_name: string;
  company_name: string;
  percent_activities_completed: number;
} */

const ModalNewAttendance: React.FC<NewAttendanceModalProps> = ({
  formRef,
  isOpen,
  onClose,
  title,
  byEmployee = false,
  getNewAttendance,
}) => {
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [dataFilters, setDataFilters] = useState<Filters>({} as Filters);
  const [loadingCreateAttendance, setLoadingCreateAttendance] = useState(false);
  const addToast = useToast();

  const handleFilters = useCallback(
    async (company_client_id?: number, demand_id?: number) => {
      setLoadingFilters(true);

      try {
        const dataFiltersAttendances = await getAttendanceFilters(
          company_client_id,
          demand_id,
          byEmployee,
        );

        setDataFilters(dataFiltersAttendances);
      } finally {
        setLoadingFilters(false);
      }
    },
    [byEmployee],
  );

  const handleCreateAttendance = useCallback(
    async (dataCreate: Attendance) => {
      try {
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          company_client_id: Yup.string().required('Selecione uma empresa'),
          demand_id: Yup.string().required('Selecione uma demanda'),
          competence: Yup.string().matches(/^(0[1-9]|1[0-2])\/\d{4}$/, {
            excludeEmptyString: true,
            message: 'Preencha a competência no formato mm/aaaa (mês e ano)',
          }),
        });

        await schema.validate(dataCreate, {
          abortEarly: false,
        });

        setLoadingCreateAttendance(true);

        const response = await api.post<Attendance>('/attendances', dataCreate);

        const attendanceData = response.data;

        addToast({
          position: 'top-right',
          isClosable: true,
          status: 'success',
          title: 'Atendimento cadastrado',
          description: 'Prossiga com o planejamento das atividades',
        });

        onClose();
        getNewAttendance(attendanceData);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);

          return;
        }

        const error = err.response?.data?.error;

        addToast({
          position: 'top-right',
          isClosable: true,
          status: 'error',
          title: 'Erro ao tentar cadastrar atendimetno',
          description: error,
        });
      } finally {
        setLoadingCreateAttendance(false);
      }
    },
    [addToast, onClose, formRef, getNewAttendance],
  );

  useEffect(() => {
    handleFilters();
  }, [handleFilters, byEmployee]);

  return (
    <ModalChakra
      title={title}
      onClose={onClose}
      isOpen={isOpen}
      titleButtonCancel="Cancelar"
      onSubmit={() => formRef.current?.submitForm()}
      isLoading={loadingCreateAttendance}
    >
      <Form onSubmit={handleCreateAttendance} ref={formRef}>
        <SelectChakra
          name="company_client_id"
          options={
            !dataFilters?.companies?.length
              ? []
              : [
                  ...dataFilters?.companies
                    ?.filter((c) => !!c.id_alterdata)
                    ?.map((comp) => ({
                      ...comp,
                      label: `${comp.id_alterdata}  - ${comp.name}`,
                      value: comp.company_id,
                    }))
                    .sort(
                      (first, second) =>
                        first.id_alterdata - second.id_alterdata,
                    ),
                  { label: '', value: '' },
                  ...dataFilters?.companies?.map((comp) => ({
                    ...comp,
                    label: comp.id_alterdata
                      ? `${comp.name}  - ${comp.id_alterdata}`
                      : comp.name,
                    value: comp.company_id,
                  })),
                ]
          }
          placeholder={loadingFilters ? 'Carregando...' : 'Selecionar Empresa'}
          required
          label="Empresa"
          mb="10px"
          width="full"
        />

        <Tooltip
          label="Ainda não há nenhuma demanda disponível para planejamento pessoal no seu departamento"
          isDisabled={!!dataFilters?.demands?.length}
        >
          <Box>
            <SelectChakra
              name="demand_id"
              options={dataFilters?.demands?.map((dem) => ({
                ...dem,
                label: dem.name,
                value: dem.id,
              }))}
              placeholder={
                loadingFilters ? 'Carregando...' : 'Selecionar Demanda'
              }
              required
              label="Demanda"
              width="full"
              disabled={!loadingFilters && !dataFilters.demands?.length}
            />
          </Box>
        </Tooltip>

        <InputChakra
          type="text"
          name="competence"
          label="Competência"
          width="103px"
          maxLength={7}
          minLength={7}
          mask="competence"
        />

        <Box>
          <Flex alignItems="center">
            <FaStream />
            <Text ml={2} color="#718096">
              Descrição
            </Text>
          </Flex>
          <TextareaChakra
            name="description"
            maxLength={250}
            placeholder="No máximo 250 caracteres"
          />
        </Box>
      </Form>
    </ModalChakra>
  );
};

export default ModalNewAttendance;
